import type { LegacyRef } from 'react';
import { forwardRef, useMemo } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';

export type ErrorStatus =
  | { tag: "Submited"; errors: string[] }
  | { tag: "Not submitted" };

  /**
   * Uncontrolled (state management stays inside MUI), 
   * works with objects, 
   * requires selection from a list, and returns an ID.
   */

export interface DropdownItemsSelectorProps {
  defaultValue?: number | null;
  onSelect: (valueId: number | null) => void;
  values: Array<{ id: number; name: string }>;
  label: string;
  icon?: string | null;
  closeIcon?: string | null;
  className?: string;
  buttonClassName?: string;
  title?: string;
  subtitle?: string | null;
  disabled: boolean;
  required?: boolean;
  open?: boolean;
  name: string;
  setOpen?: (open: boolean) => void;
  errors?: ErrorStatus;
}


/**
 * DropdownStringSelector
 * 
 * dropdown component based on MUI's `Autocomplete` for selecting a string value from a predefined list of options.
 * 
 * @param defaultValue- The initial selected value of the dropdown.
 * @param onSelect - Callback function triggered when a value is selected or cleared.
 * @param values - Array of string options available in the dropdown.
 * @param label - The label displayed above the dropdown input.
 * @param required - Whether the dropdown selection is required.
 * @param errors - Error status object used to display validation messages after form submission.
 * 
 */

function DropdownItemsSelector(
  {
    label,
    values,
    defaultValue,
    onSelect,
    errors,
    required,
    disabled
  }: DropdownItemsSelectorProps,
  ref: LegacyRef<HTMLDivElement> | undefined
) {
  // Find default item from default id.
  const defaultItem = useMemo(() => {
    return values.find((item) => item.id === defaultValue) || null;
  }, [values, defaultValue]);

  return (
    <Autocomplete
      disablePortal
      noOptionsText={`Pas d'options de sélection`}
      options={values}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      defaultValue={defaultItem}
      onChange={(_, value) => {
        onSelect(value ? value.id : null);
      }}
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          //we use required prop just for visibility, if it's not correct,
          // we will catch this error in validator of component 
          label={required ? `${label} *` : label}          
          error={errors?.tag === 'Submited' && errors.errors.length > 0}
          helperText={
            errors?.tag === 'Submited' ? errors.errors.join(', ') : ''
          }
        />
      )}
    />
  );
}

export default forwardRef(DropdownItemsSelector);
